<template>
  <q-dialog ref="modal" :persistent="persistent" @hide="hide">
    <Grid
      gtr="auto 1fr auto"
      class="r-modal bg-white-1"
      :class="{ 'full-size': fullSize }"
      :style="{ '--modalMaxHeight': maxHeight, '--modalMaxWidth': maxWidth }"
    >
      <Grid gtc="1fr auto" class="header">
        <div class="text-heading-1 text-secondary-2 q-pl-lg q-pt-lg q-pb-md">{{ title }}</div>
        <CloseIcon class="q-pt-md" style="align-items: start" @click="onCloseIcon" />
      </Grid>

      <Grid gaf="column" class="body overflow-hidden relative-position">
        <Grid v-if="menu.length" align-content="start" class="menu">
          <div
            v-for="(menuItem, index) of menu"
            :key="menuItem.label"
            class="menu-item q-pl-lg q-py-md cursor-pointer"
            :class="[
              index === menuActiveIndex
                ? 'text-heading-3 text-white-1 bg-secondary-1'
                : 'text-subtitle-1 text-secondary-1'
            ]"
            @click="onMenu(menuItem.name, index)"
          >
            {{ menuItem.label }}
          </div>
        </Grid>

        <div class="content relative-position overflow-hidden" :class="scrollClass">
          <div ref="scrollableArea" class="scrollable-area overflow-auto full-height q-pl-xl q-pr-xxl q-pb-lg">
            <slot :hide="hide" :setLoading="setLoading" :isLoading="localLoading"></slot>

            <QResizeObserver @resize="onResize" />
            <QScrollObserver @scroll="onScroll" />
          </div>

          <div class="shadow top" aria-hidden="true"></div>
          <div class="shadow bottom" aria-hidden="true"></div>
        </div>

        <q-inner-loading :showing="localLoading">
          <q-spinner color="secondary-1" size="2rem" />
        </q-inner-loading>
      </Grid>

      <Grid v-if="!noFooter" gaf="column" justify-content="end" gap="var(--r-space-md)" class="footer q-pr-xxl q-py-md">
        <slot name="footer" :props="{ hide, setLoading }">
          <RBtn :disable="localLoading" :type="cancelBtnType" @click="onCancelClicked">
            {{ cancelBtnLabel }}
          </RBtn>

          <RBtn :loading="localLoading" :type="okBtnType" @click="onOkClicked">
            {{ okBtnLabel }}
          </RBtn>
        </slot>
      </Grid>
    </Grid>
  </q-dialog>
</template>

<script>
import CloseIcon from '@/ui/icons/CloseIcon'

export default {
  components: {
    CloseIcon
  },

  props: {
    title: {
      type: String,
      required: true
    },
    loading: Boolean,
    persistent: {
      type: Boolean,
      default: true
    },
    cancelBtnLabel: {
      type: String,
      default: 'Cancel'
    },
    cancelBtnType: {
      type: String,
      default: 'ghost'
    },
    okBtnLabel: {
      type: String,
      default: 'Save'
    },
    okBtnType: {
      type: String,
      default: 'primary'
    },
    /* Array of objects:
     * [ { label: 'Basic', name: 'basic' } ]
     * The name will be used for the event emitter
     * */
    menu: {
      type: Array,
      default: () => []
    },
    fullSize: Boolean,
    noFooter: Boolean,
    controlCloseIconOnParent: Boolean,
    maxHeight: {
      type: String,
      default: '78%'
    },
    maxWidth: {
      type: String,
      default: '70%'
    }
  },

  data: () => ({
    menuActiveIndex: 0,
    isOverflowing: false,
    scrolledToBottom: false,
    scrolledToTop: false,
    localLoading: false
  }),

  computed: {
    scrollClass() {
      if (!this.isOverflowing) return null

      let shadowClasses = []

      if (this.scrolledToBottom) shadowClasses.push('shadow-top')
      else if (this.scrolledToTop) shadowClasses.push('shadow-bottom')
      else shadowClasses = ['shadow-top', 'shadow-bottom']

      return shadowClasses
    }
  },

  watch: {
    loading: {
      immediate: true,
      handler(newVal) {
        this.localLoading = newVal
      }
    }
  },

  created() {
    this.onOk = () => ({})
    this.onCancel = () => ({})
  },

  methods: {
    show() {
      this.$refs.modal.show()

      return {
        onOk: fn => (this.onOk = fn.bind(null, this.hide, this.setLoading)),
        onCancel: fn => (this.onCancel = fn.bind(null, this.hide))
      }
    },

    hide() {
      this.$emit('hide')
      this.$refs.modal.hide()
    },

    setLoading(state = true) {
      this.localLoading = state
    },

    onOkClicked() {
      this.$emit('ok', this.hide, this.setLoading)
      this.onOk()
    },

    onCancelClicked() {
      this.$emit('cancel', this.hide)
      this.onCancel()
    },

    onMenu(menuItemName, menuItemIndex) {
      this.menuActiveIndex = menuItemIndex
      this.$emit('menu', menuItemName)
    },

    onResize() {
      const $scrollableArea = this.$refs.scrollableArea

      this.isOverflowing = $scrollableArea.scrollHeight > $scrollableArea.clientHeight
    },

    onScroll() {
      if (!this.isOverflowing) return

      const $scrollableArea = this.$refs.scrollableArea

      this.scrolledToBottom = $scrollableArea.scrollTop + $scrollableArea.clientHeight === $scrollableArea.scrollHeight
      this.scrolledToTop = $scrollableArea.scrollTop === 0
    },

    onCloseIcon() {
      if (!this.controlCloseIconOnParent) this.hide()
      else this.$emit('close-icon')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/shadows';
@import '~@/styles/mixins';

.r-modal {
  max-height: var(--modalMaxHeight);
  max-width: var(--modalMaxWidth);
  @extend .shadow3;
  overflow: hidden;

  &.full-size {
    height: var(--modalMaxHeight);
    width: var(--modalMaxWidth);
  }

  .header {
    border-bottom: 1px solid var(--r-white-4);
  }

  @include custom-scrollbar('.content > .scrollable-area', var(--r-grey-3));

  .content {
    .shadow {
      bottom: 0;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 0;
      transition: all 0.2s ease-out;
    }

    &.shadow-top > .shadow.top {
      box-shadow: -6px 2px 6px 0 rgba(71, 71, 71, 0.2) inset;
    }

    &.shadow-bottom > .shadow.bottom {
      box-shadow: -6px -2px 6px 0 rgba(71, 71, 71, 0.2) inset;
    }
  }

  .footer {
    border-top: 1px solid var(--r-white-4);
  }

  .menu {
    width: 135px;
    border-right: 1px solid var(--r-white-4);
  }
}
</style>
